// Appearance (vault/Apps/Forge/Pages/Appearance.md) — WHERE A STORED NAME
// BECOMES CSS. The control plane stores three names (platform/src/
// appearance.ts's closed vocabularies); this module is the one place those
// names resolve to token values, so the palette can improve without a data
// migration and the page's live preview and the applied chrome can never
// disagree — both read this table.
//
// Every override is a `light-dark()` pair, exactly how Astryx authors the
// tokens being overridden — vite's build no longer polyfills the function
// (vite.config.ts cssTarget, the E14-t1 fix), so the pair collapses under the
// Theme's own color-scheme in both modes, applied or previewed.
//
// The DEFAULTS write NO vars at all: an org that never touched Appearance
// renders the product's stock theme, not a copy of it — absence stays absent.

export type AppearanceChoice = { base: string; accent: string; radius: string };

export const BASE_OPTIONS = [
  { value: 'astryx', label: 'Astryx' },
  { value: 'stone', label: 'Stone' },
] as const;

export const ACCENT_OPTIONS = [
  { value: 'blue', label: 'Blue' },
  { value: 'violet', label: 'Violet' },
  { value: 'teal', label: 'Teal' },
  { value: 'green', label: 'Green' },
  { value: 'orange', label: 'Orange' },
  { value: 'rose', label: 'Rose' },
] as const;

export const RADIUS_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'default', label: 'Default' },
  { value: 'round', label: 'Round' },
] as const;

/** accent name → the three tokens the brand rides on. `blue` is the stock
 *  accent, so it maps to NOTHING — see the header. */
const ACCENTS: Record<string, Record<string, string>> = {
  blue: {},
  violet: {
    '--color-accent': 'light-dark(#6d28d9, #8b74ff)',
    '--color-text-accent': 'light-dark(#6d28d9, #8b74ff)',
    '--color-accent-muted': 'light-dark(#ede9fe, #2e2650)',
  },
  teal: {
    '--color-accent': 'light-dark(#0f766e, #2dd4bf)',
    '--color-text-accent': 'light-dark(#0f766e, #2dd4bf)',
    '--color-accent-muted': 'light-dark(#ccfbf1, #113b37)',
  },
  green: {
    '--color-accent': 'light-dark(#15803d, #4ade80)',
    '--color-text-accent': 'light-dark(#15803d, #4ade80)',
    '--color-accent-muted': 'light-dark(#dcfce7, #14341f)',
  },
  orange: {
    '--color-accent': 'light-dark(#c2410c, #fb923c)',
    '--color-text-accent': 'light-dark(#c2410c, #fb923c)',
    '--color-accent-muted': 'light-dark(#ffedd5, #3d2413)',
  },
  rose: {
    '--color-accent': 'light-dark(#be123c, #fb7185)',
    '--color-text-accent': 'light-dark(#be123c, #fb7185)',
    '--color-accent-muted': 'light-dark(#ffe4e6, #3f1520)',
  },
};

/** base name → the surface family. `astryx` is stock (nothing); `stone` warms
 *  every neutral the frame is drawn with. */
const BASES: Record<string, Record<string, string>> = {
  astryx: {},
  stone: {
    '--color-background-body': 'light-dark(#f5f4f1, #191715)',
    '--color-background-surface': 'light-dark(#fbfaf8, #201d1a)',
    '--color-background-muted': 'light-dark(#edebe6, #262320)',
    '--color-background-card': 'light-dark(#fbfaf8, #201d1a)',
    '--color-border': 'light-dark(#e4e1db, #2e2a26)',
  },
};

/** radius name → the three steps this theme's controls read (src/theme.ts owns
 *  the `default` values, so `default` writes nothing). */
const RADII: Record<string, Record<string, string>> = {
  none: {
    '--radius-inner': '0px',
    '--radius-element': '0px',
    '--radius-container': '0px',
  },
  default: {},
  round: {
    '--radius-inner': '8px',
    '--radius-element': '12px',
    '--radius-container': '16px',
  },
};

/** The full var map one choice resolves to. Unknown names resolve to nothing —
 *  the SERVER refuses unknown names on save; a stale client rendering a newer
 *  org's choice degrades to stock rather than crashing. */
export function appearanceVars(choice: AppearanceChoice): Record<string, string> {
  return {
    ...(BASES[choice.base] ?? {}),
    ...(ACCENTS[choice.accent] ?? {}),
    ...(RADII[choice.radius] ?? {}),
  };
}

/** Every var name this module can ever write — what an applier must CLEAR
 *  before writing a new choice, or a switch from `stone` back to `astryx`
 *  would leave stone's surfaces behind. */
export const APPEARANCE_VAR_NAMES: ReadonlyArray<string> = [
  ...new Set(
    [...Object.values(BASES), ...Object.values(ACCENTS), ...Object.values(RADII)].flatMap((map) =>
      Object.keys(map),
    ),
  ),
];

/** Paint (or clear) a choice on an element — the document root for the applied
 *  chrome. Clears EVERY name this module can write first, so switching back to
 *  a stock value removes the override instead of leaving the old one behind. */
export function applyAppearanceToRoot(
  el: HTMLElement,
  choice: AppearanceChoice | null,
): void {
  for (const name of APPEARANCE_VAR_NAMES) el.style.removeProperty(name);
  if (!choice) return;
  for (const [name, value] of Object.entries(appearanceVars(choice))) {
    el.style.setProperty(name, value);
  }
}
